import { getCarriers, getPartners } from '@returnmates/client-core/src/selectors/admin'
import { getLocations } from '@returnmates/client-core/src/selectors/location'
import Button from '@returnmates/ui-core/src/components/Button'
import SearchToolbar from '@returnmates/ui-core/src/components/SearchToolbar'
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Context } from '../../../pages/Main'
import CheckboxFilter from '../../CheckboxFilter'
import PlusIcon from '../../images/icons/plus'
import { CONSOLIDATION_STATUSES_CHECKBOX, SEARCH_FIELDS } from '../constants'
import useStyles from '../styles'
import { ConsolidationFilter } from '../types'

interface Props {
  onFilterUpdate?: (val: ConsolidationFilter) => void
  handleOpenAddConsolidationModal: () => void
}

function Toolbar({ onFilterUpdate, handleOpenAddConsolidationModal }: Props) {
  const classes = useStyles()
  const { currentHubId } = useContext(Context)
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [searchField, setSearchField] = useState<string>('consolidationId')
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: boolean }>({})

  const partners = useSelector(getPartners)
  const carriers = useSelector(getCarriers)
  const locations = useSelector(getLocations)

  const partnerValues = useMemo<Array<{ key: string; label: string }>>(() => {
    const partnerOptions = partners.reduce((acc, partner) => {
      if (partner.code !== 'rm') {
        return [
          ...acc,
          {
            key: partner.code as string,
            label: partner.name as string,
          },
        ]
      }

      return acc
    }, [])

    return [{ key: 'rm', label: 'Returnmates' }, ...partnerOptions]
  }, [partners])

  const carrierValues = useMemo<Array<{ key: string; label: string }>>(() => {
    const carrierOptions = carriers?.map(carrier => ({
      key: carrier.id as string,
      label: carrier.name as string,
    }))

    return [...(carrierOptions || [])]
  }, [carriers])

  const locationValues = useMemo(
    () =>
      locations?.map(location => ({
        key: location.id,
        label: location.name,
      })),
    [locations],
  )

  const statusFilter = useMemo(
    () =>
      CONSOLIDATION_STATUSES_CHECKBOX.values.reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [selectedValues],
  )

  const partnerFilter = useMemo(
    () =>
      partnerValues.reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [selectedValues, partnerValues],
  )

  const carrierFilter = useMemo(
    () =>
      carrierValues.reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [selectedValues, carrierValues],
  )

  const locationFilter = useMemo(
    () =>
      (locationValues || []).reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [locationValues, selectedValues],
  )

  const handleChangeSearch = useCallback(e => {
    const searchValue = e.target.value
    setSearchValue(searchValue)
  }, [])

  const onSearchFieldChange = useCallback((searchField: string) => {
    setSearchField(searchField)
  }, [])

  const onChangeFilter = useCallback(
    newSelection => {
      setSelectedValues({
        ...selectedValues,
        ...newSelection,
      })
    },
    [selectedValues],
  )

  const updateFilters = useCallback(() => {
    onFilterUpdate?.({
      status: statusFilter,
      partnerCode: partnerFilter,
      carrierId: carrierFilter,
      locationId: locationFilter,
      [searchField]: searchValue ? searchValue : null,
    })
  }, [
    onFilterUpdate,
    statusFilter,
    partnerFilter,
    carrierFilter,
    locationFilter,
    searchField,
    searchValue,
  ])

  const clearFilters = useCallback(() => {
    setSelectedValues({})
    onFilterUpdate?.({} as ConsolidationFilter)
  }, [onFilterUpdate])

  const activeFilters = useMemo(
    () => Object.keys(selectedValues).filter(key => selectedValues[key]),
    [selectedValues],
  )

  const isClearFiltersAvailable = useMemo(() => {
    return activeFilters.length > 0
  }, [activeFilters])

  useEffect(() => {
    updateFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFilterUpdate, statusFilter, partnerFilter, carrierFilter, locationFilter])

  useEffect(() => {
    if (!searchValue?.length) {
      updateFilters()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  return (
    <div className={classes.toolbar}>
      <div className={classes.toolbarLeftPart}>
        <SearchToolbar
          value={searchValue}
          onInputChange={handleChangeSearch}
          onSearchFieldChange={onSearchFieldChange}
          isShowSearchButton
          onSearch={updateFilters}
          searchField={searchField}
          searchFields={SEARCH_FIELDS}
        />
        <div className={classes.filters}>
          <CheckboxFilter
            values={CONSOLIDATION_STATUSES_CHECKBOX.values}
            selectedValues={selectedValues}
            onChange={onChangeFilter}
            isAllAvailable
            onlyOneSelect
            label="Status"
          />
          <CheckboxFilter
            values={partnerValues}
            selectedValues={selectedValues}
            onChange={onChangeFilter}
            isAllAvailable
            onlyOneSelect
            label="Partner"
          />
          <CheckboxFilter
            values={carrierValues}
            selectedValues={selectedValues}
            onChange={onChangeFilter}
            isAllAvailable
            onlyOneSelect
            label="Carrier"
          />
          <CheckboxFilter
            values={locationValues || []}
            selectedValues={selectedValues}
            onChange={onChangeFilter}
            onlyOneSelect
            isAllAvailable
            disabled={!currentHubId}
            label="Location"
          />
          {isClearFiltersAvailable ? (
            <Button label="Clear Filters" className={classes.clearFilters} onClick={clearFilters} />
          ) : null}
        </div>
      </div>
      <div className={classes.toolbarRightPart}>
        <Button
          label="Add Consolidation"
          onClick={handleOpenAddConsolidationModal}
          startIcon={<PlusIcon className={classes.plusIcon} />}
          className={classes.addConsolidationButton}
        />
      </div>
    </div>
  )
}

export default memo(Toolbar)
